import Request from './request-service';

const DOCUMENT_IMAGE_KEY_PATTERN = /\/document-images\//;
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+\-.]*:/i;

// The backend signs URLs for DOCUMENT_IMAGE_SIGNED_URL_TTL_SECONDS = 60 * 60 (documentController.ts).
// Cache well under that so a client never serves a URL past the point it actually expires.
const SIGNED_URL_CACHE_TTL_MS = 45 * 60 * 1000;

interface CacheEntry {
  url: string;
  expiresAt: number;
  inflight?: Promise<string>;
}

const cache = new Map<string, CacheEntry>();

export const isDocumentImageKey = (value: string | null | undefined): value is string => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed || ABSOLUTE_URL_PATTERN.test(trimmed)) return false;
  return DOCUMENT_IMAGE_KEY_PATTERN.test(trimmed);
};

export const seedDocumentImageUrl = (key: string, signedUrl: string) => {
  cache.set(key, { url: signedUrl, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS });
};

export const resolveDocumentImageUrl = async (
  key: string,
  options: { force?: boolean } = {},
): Promise<string> => {
  const existing = cache.get(key);
  if (!options.force && existing) {
    if (existing.inflight) return existing.inflight;
    if (existing.expiresAt > Date.now()) return existing.url;
  }

  const inflight = (async () => {
    const response = await Request.post('content/document/image/resolve', { key });
    if (!response) throw new Error('No response from server');
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error ?? 'Failed to resolve document image');
    }

    const data = await response.json();
    if (typeof data?.signedUrl !== 'string' || !data.signedUrl) {
      throw new Error('Resolve response missing signedUrl');
    }

    cache.set(key, { url: data.signedUrl, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS });
    return data.signedUrl as string;
  })();

  cache.set(key, { url: existing?.url ?? '', expiresAt: existing?.expiresAt ?? 0, inflight });

  try {
    return await inflight;
  } catch (error) {
    cache.delete(key);
    throw error;
  }
};
